import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { TabItem } from './TabItem';
import { TabOverflowMenu } from './TabOverflowMenu';
const tabBarStyle = {
    display: 'flex',
    alignItems: 'stretch',
    backgroundColor: '#16171e',
    borderBottom: '1px solid #2a2d3a',
    height: 38,
    flexShrink: 0,
    position: 'relative',
    userSelect: 'none',
};
const scrollBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    background: 'none',
    border: 'none',
    color: '#787c99',
    cursor: 'pointer',
    fontSize: 16,
    flexShrink: 0,
    padding: 0,
};
const tabsContainerStyle = {
    display: 'flex',
    alignItems: 'stretch',
    overflowX: 'auto',
    flex: 1,
    scrollBehavior: 'smooth',
    /* Hide scrollbar */
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
};
const newTabBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    background: 'none',
    border: 'none',
    borderLeft: '1px solid #2a2d3a',
    color: '#787c99',
    cursor: 'pointer',
    fontSize: 18,
    fontWeight: 300,
    flexShrink: 0,
    padding: 0,
    transition: 'color 0.15s, background-color 0.15s',
};
const overflowBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    background: 'none',
    border: 'none',
    borderLeft: '1px solid #2a2d3a',
    color: '#787c99',
    cursor: 'pointer',
    fontSize: 16,
    flexShrink: 0,
    padding: 0,
    position: 'relative',
};
/**
 * Terminal tab bar component
 * Manages tab display, scrolling, and overflow
 */
export const TabBar = ({ sessions, activeSessionId, onTabActivate, onTabClose, onTabRename, onNewTab, maxTabs, }) => {
    const tabsContainerRef = useRef(null);
    const [showLeftScroll, setShowLeftScroll] = useState(false);
    const [showRightScroll, setShowRightScroll] = useState(false);
    const [showOverflowMenu, setShowOverflowMenu] = useState(false);
    // Check if scrolling is needed
    useEffect(() => {
        const checkScroll = () => {
            const container = tabsContainerRef.current;
            if (!container)
                return;
            const { scrollLeft, scrollWidth, clientWidth } = container;
            setShowLeftScroll(scrollLeft > 0);
            setShowRightScroll(scrollLeft + clientWidth < scrollWidth);
        };
        checkScroll();
        const container = tabsContainerRef.current;
        container?.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);
        return () => {
            container?.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, [sessions]);
    // Scroll to active tab when it changes
    useEffect(() => {
        if (activeSessionId && tabsContainerRef.current) {
            const activeTab = tabsContainerRef.current.querySelector(`[data-session-id="${activeSessionId}"]`);
            if (activeTab && activeTab.scrollIntoView) {
                activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    }, [activeSessionId]);
    const handleScroll = (direction) => {
        const container = tabsContainerRef.current;
        if (!container)
            return;
        const scrollAmount = 200;
        container.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
        });
    };
    const canCreateNewTab = sessions.length < maxTabs;
    // Sort sessions by order
    const sortedSessions = [...sessions].sort((a, b) => a.order - b.order);
    return (_jsxs("div", { style: tabBarStyle, children: [showLeftScroll && (_jsx("button", { style: scrollBtnStyle, onClick: () => handleScroll('left'), "aria-label": "Scroll tabs left", children: "\u2039" })), _jsx("div", { style: tabsContainerStyle, ref: tabsContainerRef, children: sortedSessions.map((session) => (_jsx(TabItem, { session: session, isActive: session.id === activeSessionId, onActivate: onTabActivate, onClose: onTabClose, onRename: onTabRename }, session.id))) }), showRightScroll && (_jsx("button", { style: scrollBtnStyle, onClick: () => handleScroll('right'), "aria-label": "Scroll tabs right", children: "\u203A" })), canCreateNewTab && (_jsx("button", { style: newTabBtnStyle, onClick: onNewTab, "aria-label": "Create new terminal", title: "New Terminal (Ctrl+Shift+T)", onMouseEnter: (e) => {
                    e.currentTarget.style.color = '#a9b1d6';
                    e.currentTarget.style.backgroundColor = '#1e2030';
                }, onMouseLeave: (e) => {
                    e.currentTarget.style.color = '#787c99';
                    e.currentTarget.style.backgroundColor = 'transparent';
                }, children: "+" })), sessions.length > 5 && (_jsxs("div", { style: { position: 'relative', flexShrink: 0 }, children: [_jsx("button", { style: overflowBtnStyle, onClick: () => setShowOverflowMenu(!showOverflowMenu), "aria-label": "Show all terminals", title: "All Terminals", children: "\u22EE" }), showOverflowMenu && (_jsx(TabOverflowMenu, { sessions: sortedSessions, activeSessionId: activeSessionId, onSelect: (id) => {
                            onTabActivate(id);
                            setShowOverflowMenu(false);
                        }, onClose: () => setShowOverflowMenu(false) }))] }))] }));
};
//# sourceMappingURL=TabBar.js.map